import type { ReactNode } from "react";
import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";

import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { useLibrary } from "../../../providers/useLibrary.hook";
import { centsToDollars } from "../../../utils";
import { useWirePermissions } from "../wireTemplates/hooks/permissions";

import { useFetchSupportedCurrencies } from "./requests";

import type { components } from "./schema";

export type InternationalWire = components["schemas"]["InternationalWire"];

export function createInternationalWireFromAPISwiftTransaction(
  apiSwiftTransaction: API.SwiftTransaction,
): InternationalWire {
  const {
    id,
    amount,
    from_account,
    destination,
    recipient,
    created_at,
    updated_at,
    wire_reason,
    memo,
    note,
    state,
    quote,
    template_name,
  } = apiSwiftTransaction;
  return {
    id,
    amount: centsToDollars(amount),
    fromAccountId: from_account,
    swiftDestinationId: destination,
    createdAt: created_at,
    updatedAt: updated_at,
    wireReason: wire_reason,
    recipientId: recipient,
    memo,
    note,
    state,
    quote,
    templateName: template_name,
    startDate: "",
    recurringRule: {
      isVisible: false,
      ends: "",
    },
  };
}

type InternationalWiresContext = {
  isLoading: boolean;
  data: InternationalWire[];
  error?: Error;
  refresh: () => void;
};

export const internationalWiresContext =
  createContext<InternationalWiresContext>({
    isLoading: false,
    data: [],
    error: undefined,
    refresh: () => {},
  });

export const useInternationalWiresContext = () =>
  useContext(internationalWiresContext);

export const InternationalWiresProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { fetchApi, token } = useLibrary("network");
  const { internationalWires } = useWirePermissions();
  const queryFn = () => {
    if (!token || !internationalWires) {
      return Promise.resolve([] as InternationalWire[]);
    }
    return fetchApi("swift_transactions")
      .then((response) => response.json())
      .then((responseData) => responseData.results as API.SwiftTransaction[])
      .then((apiSwiftTransactions) =>
        apiSwiftTransactions.map(
          createInternationalWireFromAPISwiftTransaction,
        ),
      );
  };
  const { isLoading, data, error } = useQuery({
    queryKey: [
      "swift_transactions",
      lastUpdateRequested.getTime(),
      internationalWires,
      fetchApi,
      token,
    ],
    queryFn,
    placeholderData: (previousData, previousQuery) =>
      previousQuery?.queryKey[4] === token
        ? keepPreviousData(previousData)
        : undefined,
  });
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const value = useMemo(
    () => ({
      isLoading,
      data: data || [],
      error: error || undefined,
      refresh,
    }),
    [isLoading, data, error, refresh],
  );
  return (
    <internationalWiresContext.Provider value={value}>
      {children}
    </internationalWiresContext.Provider>
  );
};

type ScheduledInternationalWiresContext = {
  isLoading: boolean;
  data: API.ScheduledSwiftWireTransfer[];
  error?: Error;
  refresh: () => void;
};

export const scheduledInternationalWiresContext =
  createContext<ScheduledInternationalWiresContext>({
    isLoading: false,
    data: [],
    error: undefined,
    refresh: () => {},
  });

export const useScheduledInternationalWiresContext = () =>
  useContext(scheduledInternationalWiresContext);

export const ScheduledInternationalWiresProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [lastUpdateRequested, setLastUpdateRequested] = React.useState<Date>(
    new Date(),
  );
  const { fetchApi, token } = useLibrary("network");
  const { consumerInternationalWires: internationalWires } =
    useWirePermissions();
  const queryFn = () => {
    if (!token || !internationalWires) {
      return Promise.resolve([] as API.ScheduledSwiftWireTransfer[]);
    }
    const fetchPage = async (
      url: string,
    ): Promise<API.ScheduledSwiftWireTransfer[]> => {
      const response = await fetchApi(url);
      const { results, next } =
        (await response.json()) as API.PaginatedResults<API.ScheduledSwiftWireTransfer>;
      if (next) {
        const nextWires = await fetchPage(next);
        results.push(...nextWires);
      }
      return results;
    };
    return fetchPage("swift_transactions/scheduled/");
  };
  const { isLoading, data, error } = useQuery({
    queryKey: [
      "swift_transactions_scheduled",
      lastUpdateRequested.getTime(),
      internationalWires,
      fetchApi,
      token,
    ],
    queryFn,
  });
  const refresh = useCallback(() => setLastUpdateRequested(new Date()), []);
  const value = useMemo(
    () => ({
      isLoading,
      data: data || [],
      error: error || undefined,
      refresh,
    }),
    [isLoading, data, error, refresh],
  );
  return (
    <scheduledInternationalWiresContext.Provider value={value}>
      {children}
    </scheduledInternationalWiresContext.Provider>
  );
};

type CurrencyContextType = {
  currencies: Array<API.CurrencyItem> | null;
  getCurrencies: () => Promise<Array<API.CurrencyItem> | undefined>;
  selectedCurrency: API.CurrencyItem | null;
  setSelectedCurrency: (currency: API.CurrencyItem | null) => void;
};
const currencyContext = createContext<CurrencyContextType>({
  currencies: null,
  getCurrencies: () => Promise.resolve([] as Array<API.CurrencyItem>),
  selectedCurrency: null,
  setSelectedCurrency: () => {},
});
export const useCurrencyContext = () => useContext(currencyContext);

export const CurrencyProvider = (props: { children: React.ReactNode }) => {
  const [currencies, setCurrencies] = useState<Array<API.CurrencyItem> | null>(
    null,
  );
  const [selectedCurrency, setSelectedCurrency] =
    useState<API.CurrencyItem | null>(null);
  const { fetchSupportedCurrencies } = useFetchSupportedCurrencies();
  const setNoCurrencies = () => {
    // we fetched currencies and there were none
    setCurrencies([]);
    return [];
  };
  const getCurrencies: () => Promise<Array<API.CurrencyItem> | undefined> =
    useCallback(() => {
      if (currencies) return Promise.resolve(currencies);
      return fetchSupportedCurrencies()
        .then((response) => {
          if (!response) {
            return setNoCurrencies();
          }
          setCurrencies(response);
          return response;
        })
        .catch(setNoCurrencies);
    }, [currencies, fetchSupportedCurrencies]);

  return (
    <currencyContext.Provider
      value={{
        currencies,
        getCurrencies,
        selectedCurrency,
        setSelectedCurrency,
      }}
    >
      {props.children}
    </currencyContext.Provider>
  );
};
